import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useProjectsForSelect } from '@/hooks/useProjectsQuery';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import { Stakeholder, StakeholderAttachment } from '@/pages/admin/StakeholderManagement';
import { supabase } from '@/integrations/supabase/client';
import { uploadStakeholderFile, deleteStakeholderFile } from '@/lib/storage';
import { StakeholderFileUpload } from './StakeholderFileUpload';
import { useStakeholderInteractions, useStakeholderFollowUps } from '@/hooks/useStakeholderInteractions';
import { format } from 'date-fns';
import { Plus, Calendar as CalendarIcon, MessageSquare, Edit } from 'lucide-react';
import { getInteractionTypeColor } from '@/lib/stakeholders/utils';
import { toast } from 'sonner';
import { InteractionDialog } from '@/components/stakeholders/dialogs/InteractionDialog';
import { FollowUpDialog } from '@/components/stakeholders/dialogs/FollowUpDialog';
import { EditFollowUpDialog } from '@/components/stakeholders/dialogs/EditFollowUpDialog';
import { EditInteractionDialog } from '@/components/stakeholders/dialogs/EditInteractionDialog';
import { useSOLUsers } from '@/hooks/useSOLUsers';

type StakeholderFormData = {
  name: string;
  email?: string;
  phone?: string;
  role: string;
  project_id?: string;
  project_group?: string;
  project_type?: string;
  notes?: string;
  created_by?: string;
};

interface FileWithPreview {
  file: File;
  preview?: string;
  name: string;
  type: string;
  size: number;
  id: string;
}

interface StakeholderEditDialogProps {
  stakeholder: Stakeholder | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  contextProjectId?: string;
  onDelete?: (stakeholder: Stakeholder) => void;
}

export function StakeholderEditDialog({ stakeholder, isOpen, onClose, onSuccess, contextProjectId, onDelete }: StakeholderEditDialogProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newFiles, setNewFiles] = useState<FileWithPreview[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<StakeholderAttachment[]>([]);
  const [deletedPaths, setDeletedPaths] = useState<string[]>([]);
  const { data: projects = [] } = useProjectsForSelect();
  const [extraProject, setExtraProject] = useState<{ id: string; project_name: string } | null>(null);
  const [contextProjectName, setContextProjectName] = useState<string>('');
  const [isAddInteractionOpen, setIsAddInteractionOpen] = useState(false);
  const [isAddFollowUpOpen, setIsAddFollowUpOpen] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState<any>(null);
  const [editingInteraction, setEditingInteraction] = useState<any>(null);
  const [isEditFollowUpOpen, setIsEditFollowUpOpen] = useState(false);
  const [isEditInteractionOpen, setIsEditInteractionOpen] = useState(false);
  const [projectGroups, setProjectGroups] = useState<string[]>([]);
  const [projectTypes, setProjectTypes] = useState<string[]>([]);
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  
  const draftKey = stakeholder ? `stakeholder-edit-form-draft-${stakeholder.id}` : '';
  const hasLoadedDraftRef = useRef(false);
  
  const { data: interactions = [], isLoading: isLoadingInteractions } = useStakeholderInteractions(
    stakeholder ? [stakeholder.id] : []
  );
  
  const { data: followUps = [], refetch: refetchFollowUps } = useStakeholderFollowUps(
    stakeholder ? [stakeholder.id] : []
  );
  
  const { data: solUsers = [] } = useSOLUsers();

  const stakeholderSchema = useMemo(() =>
    z.object({
      name: z.string().trim().min(1, 'Name is required'),
      email: z.union([
        z.string().trim().email('Invalid email'),
        z.literal('')
      ]).optional(),
      phone: z.string().trim().optional(),
      role: z.string().trim().optional(),
      project_id: z.string().trim().optional(),
      project_group: z.string().trim().optional(),
      project_type: z.string().trim().optional(),
      notes: z.string().optional(),
      created_by: z.string().uuid().optional(),
    }),
    [contextProjectId]
  );

  const form = useForm<StakeholderFormData>({
    resolver: zodResolver(stakeholderSchema),
    mode: 'onChange', // ✅ Real-time validation for immediate button enable
    criteriaMode: 'all',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      role: '',
      project_id: contextProjectId || '',
      project_group: '',
      project_type: '',
      notes: '',
      created_by: 'none',
    },
  });

  // ✅ PHASE 1: Split useEffect to avoid race conditions
  // Effect 1: Reset form when stakeholder changes
  useEffect(() => {
    if (stakeholder && isOpen && draftKey && !hasLoadedDraftRef.current) {
      const draft = getStandardValue<StakeholderFormData>(draftKey, null as any);
      if (draft) {
        form.reset(draft);
      } else {
        form.reset({
          name: stakeholder.name,
          email: stakeholder.email || '',
          phone: stakeholder.phone || '',
          role: stakeholder.role,
          project_id: stakeholder.project_id || '',
          project_group: (stakeholder as any).project_group || '',
          project_type: (stakeholder as any).project_type || '',
          notes: stakeholder.notes || '',
          created_by: stakeholder.created_by || 'none',
        });
      }
      setExistingAttachments(stakeholder.attachments || []);
      setNewFiles([]);
      setDeletedPaths([]);
      
      // ✅ Trigger validation immediately after reset to update isValid
      setTimeout(() => {
        form.trigger(undefined, { shouldFocus: false });
      }, 0);
      
      hasLoadedDraftRef.current = true;
    }
    
    // Reset flag when dialog closes
    if (!isOpen) {
      hasLoadedDraftRef.current = false;
    }
  }, [stakeholder?.id, isOpen, draftKey, getStandardValue, form]);
  
  // Auto-save draft
  useEffect(() => {
    if (!isOpen || !draftKey) return;
    const subscription = form.watch((values) => {
      setStandardValue(draftKey, values as StakeholderFormData);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, form, draftKey, setStandardValue]);

  // Effect: Fetch distinct groups and types from both projects and stakeholders
  useEffect(() => {
    if (!isOpen) return;

    const fetchDistinctValues = async () => {
      // Fetch from projects table
      const { data: projectsData } = await supabase
        .from('projects')
        .select('project_group, project_type');
      
      // Fetch from stakeholders table (only unassigned ones)
      const { data: stakeholdersData } = await supabase
        .from('stakeholders')
        .select('project_group, project_type');

      // Combine and deduplicate
      const allData = [...(projectsData || []), ...(stakeholdersData || [])];
      
      const uniqueGroups = Array.from(new Set(
        allData.map(d => d.project_group).filter(Boolean)
      )).sort();
      
      const uniqueTypes = Array.from(new Set(
        allData.map(d => d.project_type).filter(Boolean)
      )).sort();

      setProjectGroups(uniqueGroups);
      setProjectTypes(uniqueTypes);
    };

    fetchDistinctValues();
  }, [isOpen]);

  // Effect 2: Fetch missing project (only when needed)
  useEffect(() => {
    if (!stakeholder?.project_id || !isOpen) {
      setExtraProject(null);
      return;
    }

    if (projects.find(p => p.id === stakeholder.project_id)) {
      setExtraProject(null);
      return;
    }

    const abortController = new AbortController();
    
    supabase
      .from('projects')
      .select('id, project_name')
      .eq('id', stakeholder.project_id)
      .abortSignal(abortController.signal)
      .single()
      .then(({ data, error }) => {
        if (error && error.name !== 'AbortError') {
          console.error('[StakeholderEditDialog] Failed to fetch project:', error);
          return;
        }
        if (data) {
          setExtraProject(data);
        }
      });

    return () => abortController.abort();
  }, [stakeholder?.project_id, isOpen, projects]);

  // Effect 3: Fetch context project (only when contextProjectId changes)
  useEffect(() => {
    if (!contextProjectId || !isOpen) {
      setContextProjectName('');
      return;
    }

    const abortController = new AbortController();

    supabase
      .from('projects')
      .select('project_name')
      .eq('id', contextProjectId)
      .abortSignal(abortController.signal)
      .single()
      .then(({ data, error }) => {
        if (error && error.name !== 'AbortError') {
          console.error('[StakeholderEditDialog] Failed to fetch context project:', error);
          return;
        }
        if (data) {
          setContextProjectName(data.project_name);
        }
      });

    return () => abortController.abort();
  }, [contextProjectId, isOpen]);

  // Effect 4: Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      setNewFiles([]);
      setExistingAttachments([]);
      setDeletedPaths([]);
      setExtraProject(null);
      setContextProjectName('');
    }
  }, [isOpen]);

  const handleDeleteExisting = (path: string) => {
    setDeletedPaths(prev => [...prev, path]);
    setExistingAttachments(prev => prev.filter(f => f.path !== path));
  };

  const onSubmit = async (data: StakeholderFormData) => {
    if (!stakeholder) return;
    if (isSubmitting) return; // Guard double-submit
    
    // Determine target project
    const targetProjectId = contextProjectId ?? data.project_id;
    
    setIsSubmitting(true);
    if (import.meta.env.DEV) {
      console.log(`[StakeholderEditDialog] Updating stakeholder ${stakeholder.id} for project ${targetProjectId}`);
    }
    try {
      for (const path of deletedPaths) {
        try {
          await deleteStakeholderFile(path);
        } catch (error) {
          console.error(`Failed to delete file ${path}:`, error);
        }
      }

      // ✅ PHASE 1: Upload with retry mechanism (3 attempts, 200ms delay)
      const newAttachments: StakeholderAttachment[] = [];
      const failedFiles: string[] = [];
      
      for (const fileData of newFiles) {
        let uploaded = false;
        
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const result = await uploadStakeholderFile(stakeholder.id, fileData.file);
            newAttachments.push(result);
            uploaded = true;
            if (import.meta.env.DEV) {
              console.debug('[StakeholderEditDialog] Upload success:', { 
                fileName: fileData.name, 
                stakeholderId: stakeholder.id,
                path: result.path 
              });
            }
            break;
          } catch (error) {
            console.error('[StakeholderEditDialog] Upload attempt failed:', {
              attempt: attempt + 1,
              fileName: fileData.name,
              stakeholderId: stakeholder.id,
              error: error instanceof Error ? error.message : error
            });
            
            if (attempt === 2) {
              failedFiles.push(fileData.name);
              toast.error(`Failed to upload: ${fileData.name}. Please check your permissions.`);
            } else {
              await new Promise(r => setTimeout(r, 200));
            }
          }
        }
      }
      
      if (failedFiles.length > 0 && import.meta.env.DEV) {
        console.warn('[StakeholderEditDialog] Some files failed to upload:', failedFiles);
      }

      const finalAttachments = [
        ...existingAttachments,
        ...newAttachments
      ];

      // Update stakeholder (and optionally sync project_id if not in context)
      const hasProject = !!(contextProjectId ?? data.project_id);
      const updatePayload: any = {
        name: data.name,
        email: data.email || null,
        phone: data.phone || null,
        notes: data.notes || null,
        attachments: finalAttachments as any,
        created_by: data.created_by === 'none' ? null : data.created_by,
        // Only save group/type to stakeholders table if no project assigned
        project_group: hasProject ? null : (data.project_group || null),
        project_type: hasProject ? null : (data.project_type || null),
      };
      
      const { error: stakeholderError } = await supabase
        .from('stakeholders')
        .update(updatePayload)
        .eq('id', stakeholder.id);

      if (stakeholderError) {
      if (import.meta.env.DEV) {
        console.error('[StakeholderEditDialog] Stakeholder update failed:', stakeholderError);
      }
        throw stakeholderError;
      }

      if (import.meta.env.DEV) {
        console.log('[StakeholderEditDialog] Stakeholder updated, upserting assignment...');
      }

      // Only upsert assignment if targetProjectId exists
      if (targetProjectId) {
        const { error: assignmentError } = await supabase
          .from('project_stakeholder_assignments')
          .upsert(
            {
              stakeholder_id: stakeholder.id,
              project_id: targetProjectId,
              role: data.role || 'Stakeholder',
            },
            { onConflict: 'project_id,stakeholder_id' }
          );

        if (assignmentError) {
          if (import.meta.env.DEV) {
            console.error('[StakeholderEditDialog] Assignment upsert failed:', assignmentError);
          }
          throw assignmentError;
        }
      }

      // ✅ PHASE 2: Optimized cache invalidation (single pattern match)
      await queryClient.invalidateQueries({ 
        predicate: (query) => 
          query.queryKey[0] === 'stakeholders' || 
          (query.queryKey[0] === 'project-stakeholders' && query.queryKey[1] === targetProjectId)
      });

      if (import.meta.env.DEV) {
        console.log('[StakeholderEditDialog] Caches invalidated');
      }

      toast.success('Stakeholder updated successfully');
      if (draftKey) removeStandardValue(draftKey);
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error updating stakeholder:', error);
      toast.error('Failed to update stakeholder');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (draftKey) removeStandardValue(draftKey);
    form.reset();
    setNewFiles([]);
    setExistingAttachments([]);
    setDeletedPaths([]);
    setIsAddInteractionOpen(false);
    setIsAddFollowUpOpen(false);
    setEditingFollowUp(null);
    setEditingInteraction(null);
    setIsEditFollowUpOpen(false);
    setIsEditInteractionOpen(false);
    onClose();
  };

  const handleAddInteraction = () => {
    setIsAddInteractionOpen(true);
  };

  const handleAddFollowUp = () => {
    setIsAddFollowUpOpen(true);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent key={stakeholder?.id || 'new'} className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Stakeholder</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Thông tin</TabsTrigger>
              <TabsTrigger value="history">Sự kiện</TabsTrigger>
            </TabsList>
            
            {/* Tab 1: Existing form */}
            <TabsContent value="info" className="space-y-4 mt-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter stakeholder name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter email" type="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter phone number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter role" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="created_by"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lead (SOL/SOLB)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Lead" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">-- None --</SelectItem>
                            {solUsers.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {contextProjectId ? (
                    <div className="space-y-2">
                      <Label>Project *</Label>
                      <div className="px-3 py-2 border rounded-md bg-muted text-muted-foreground">
                        {contextProjectName || 'Loading...'}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Cannot change project from this context
                      </p>
                    </div>
                  ) : (
                    <FormField
                      control={form.control}
                      name="project_id"
                      shouldUnregister={!!contextProjectId}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Project</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select project" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {extraProject && !projects.find(p => p.id === extraProject.id) && (
                                <SelectItem key={extraProject.id} value={extraProject.id}>
                                  {extraProject.project_name}
                                </SelectItem>
                              )}
                              {projects.map((project) => (
                                <SelectItem key={project.id} value={project.id}>
                                  {project.project_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Project Group - Editable if no project */}
                  <FormField
                    control={form.control}
                    name="project_group"
                    render={({ field }) => {
                      const hasProject = !!(form.watch('project_id') || contextProjectId);
                      const isReadOnly = hasProject;
                      
                      return (
                        <FormItem>
                          <FormLabel>Group</FormLabel>
                          {isReadOnly ? (
                            <div className="px-3 py-2 border rounded-md bg-muted text-muted-foreground text-sm">
                              {(stakeholder as any)?.project_group || '-'}
                            </div>
                          ) : (
                            <Select
                              disabled={isReadOnly}
                              value={field.value || '__none__'}
                              onValueChange={(value) => field.onChange(value === '__none__' ? null : value)}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select group" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {projectGroups.map(group => (
                                  <SelectItem key={group} value={group}>
                                    {group}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {isReadOnly 
                              ? 'Automatically set based on project' 
                              : 'Select group (will be overridden when project is assigned)'}
                          </p>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  {/* Project Type - Editable if no project */}
                  <FormField
                    control={form.control}
                    name="project_type"
                    render={({ field }) => {
                      const hasProject = !!(form.watch('project_id') || contextProjectId);
                      const isReadOnly = hasProject;
                      
                      return (
                        <FormItem>
                          <FormLabel>Type</FormLabel>
                          {isReadOnly ? (
                            <div className="px-3 py-2 border rounded-md bg-muted text-muted-foreground text-sm">
                              {(stakeholder as any)?.project_type || '-'}
                            </div>
                          ) : (
                            <Select
                              disabled={isReadOnly}
                              value={field.value || '__none__'}
                              onValueChange={(value) => field.onChange(value === '__none__' ? null : value)}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {projectTypes.map(type => (
                                  <SelectItem key={type} value={type}>
                                    {type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {isReadOnly 
                              ? 'Automatically set based on project' 
                              : 'Select type (will be overridden when project is assigned)'}
                          </p>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter notes about this stakeholder..."
                            className="min-h-[100px] resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Attachments</label>
                    <StakeholderFileUpload
                      stakeholderId={stakeholder?.id}
                      existingFiles={existingAttachments}
                      value={newFiles}
                      onFilesChange={setNewFiles}
                      onDeleteExisting={handleDeleteExisting}
                      disabled={isSubmitting || !stakeholder?.id}
                    />
                  </div>

                  <div className="flex justify-between gap-3 pt-4">
                    {/* Left side: Delete button (destructive) */}
                    {onDelete && stakeholder && (
                      <Button 
                        type="button" 
                        variant="destructive" 
                        onClick={() => onDelete(stakeholder)}
                        disabled={isSubmitting}
                      >
                        Delete Stakeholder
                      </Button>
                    )}
                    
                    {/* Right side: Cancel & Update */}
                    <div className="flex gap-3 ml-auto">
                      <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Updating...' : 'Update'}
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
            </TabsContent>
            
            {/* Tab 2: Interactions & Follow-ups */}
            <TabsContent value="history" className="space-y-4 mt-4">
              <div className="space-y-4">
                {/* Follow-ups Section */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      Follow-ups
                    </h3>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleAddFollowUp}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Thêm Follow-up
                    </Button>
                  </div>
                  
                  <ScrollArea className="h-[150px] border rounded-lg p-3">
                    {followUps.length > 0 ? (
                      <div className="space-y-2">
                        {followUps.map((followUp) => (
                          <div 
                            key={followUp.id} 
                            className="p-2 border rounded-md flex items-center justify-between hover:bg-muted/50 transition-colors"
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                  {format(new Date(followUp.follow_up_date), 'dd/MM/yyyy')}
                                </span>
                                <Badge 
                                  variant={followUp.status === 'completed' ? 'secondary' : 'default'}
                                >
                                  {followUp.status === 'pending' ? 'Upcoming' : 
                                   followUp.status === 'completed' ? 'Completed' : 'Cancelled'}
                                </Badge>
                              </div>
                              {followUp.notes && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                  {followUp.notes}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingFollowUp(followUp);
                                setIsEditFollowUpOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Chưa có follow-up nào</p>
                    )}
                  </ScrollArea>
                </div>
                
                {/* Interactions Section */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Tương tác ({interactions.length})
                    </h3>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleAddInteraction}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ghi nhận Tương tác
                    </Button>
                  </div>
                  
                  <ScrollArea className="h-[200px] border rounded-lg p-3">
                    {isLoadingInteractions ? (
                      <p className="text-sm text-muted-foreground">Đang tải...</p>
                    ) : interactions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Chưa có tương tác nào</p>
                    ) : (
                      <div className="space-y-2">
                        {interactions.map((interaction) => (
                          <div 
                            key={interaction.id} 
                            className="p-3 border rounded-md hover:bg-muted/50 transition-colors flex items-start justify-between"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge 
                                  variant="outline"
                                  className={getInteractionTypeColor(interaction.type)}
                                >
                                  {interaction.type}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(interaction.date), 'dd/MM/yyyy HH:mm')}
                                </span>
                              </div>
                              <p className="text-sm font-medium">{interaction.subject}</p>
                              {interaction.outcome && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {interaction.outcome}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingInteraction(interaction);
                                setIsEditInteractionOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Interaction Dialog */}
      {stakeholder && (
        <InteractionDialog
          isOpen={isAddInteractionOpen}
          onClose={() => setIsAddInteractionOpen(false)}
          stakeholderId={stakeholder.id}
          stakeholderName={stakeholder.name}
        />
      )}

      {/* Follow-up Dialog */}
      {stakeholder && (
        <FollowUpDialog
          isOpen={isAddFollowUpOpen}
          onClose={() => setIsAddFollowUpOpen(false)}
          stakeholderId={stakeholder.id}
          stakeholderName={stakeholder.name}
          currentFollowUp={stakeholder.next_follow_up || stakeholder.nextFollowUp}
        />
      )}

      {/* Edit Follow-up Dialog */}
      {stakeholder && (
        <EditFollowUpDialog
          followUp={editingFollowUp}
          stakeholders={[]}
          isOpen={isEditFollowUpOpen}
          onClose={() => {
            setIsEditFollowUpOpen(false);
            setEditingFollowUp(null);
          }}
          onSuccess={() => {
            refetchFollowUps();
            queryClient.invalidateQueries({ queryKey: ['stakeholders'] });
            setIsEditFollowUpOpen(false);
            setEditingFollowUp(null);
            toast.success('Follow-up đã được cập nhật');
          }}
        />
      )}

      {/* Edit Interaction Dialog */}
      {stakeholder && (
        <EditInteractionDialog
          interaction={editingInteraction}
          stakeholders={[]}
          isOpen={isEditInteractionOpen}
          onClose={() => {
            setIsEditInteractionOpen(false);
            setEditingInteraction(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['stakeholder-interactions'] });
            setIsEditInteractionOpen(false);
            setEditingInteraction(null);
            toast.success('Tương tác đã được cập nhật');
          }}
        />
      )}
    </>
  );
}